import React, { useEffect, useMemo, useState, useCallback } from "react";
import Layout from "../../components/Layout";
import { Plus, Search, Pencil, Trash2, X } from "lucide-react";
import "../../styles/member/categories.css";
import { CategoriesService, Category } from "../../services/categoriesService";
import ConfirmDelete from "../../components/ConfirmDelete";
import emptyDataImg from "../../assets/icons/empty-data.svg";

const DEFAULT_LIMIT = 10;

const Categories: React.FC = () => {
  const [items, setItems] = useState<Category[]>([]);
  const [total, setTotal] = useState<number>(0);
  const [page, setPage] = useState<number>(1);
  const [limit] = useState<number>(DEFAULT_LIMIT);
  const [search, setSearch] = useState<string>("");
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string>("");

  const [showForm, setShowForm] = useState<boolean>(false);
  const [form, setForm] = useState<{
    id?: number;
    name: string;
    slug: string;
    description: string;
    isActive: boolean;
  }>({
    name: "",
    slug: "",
    description: "",
    isActive: true,
  });
  const [slugTouched, setSlugTouched] = useState<boolean>(false);
  const [showDelete, setShowDelete] = useState<boolean>(false);
  const [deleteId, setDeleteId] = useState<number | null>(null);
  const [selectedIds, setSelectedIds] = useState<number[]>([]);
  const allSelected = useMemo(
    () => items.length > 0 && selectedIds.length === items.length,
    [items.length, selectedIds]
  );

  const toggleSelectAll = () => {
    if (allSelected) setSelectedIds([]);
    else setSelectedIds(items.map((i) => i.id));
  };

  const toggleSelect = (id: number) => {
    setSelectedIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
  };

  const isEditing = useMemo(() => typeof form.id === "number", [form.id]);
  const totalPages = useMemo(
    () => Math.max(1, Math.ceil(total / limit)),
    [total, limit]
  );

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const res = await CategoriesService.listCategories({
        page,
        limit,
        search: search.trim() || undefined,
      });
      setItems(res.data);
      setTotal(res.total);
    } catch (e: any) {
      setError(e?.message || "Lỗi tải danh sách danh mục");
    } finally {
      setLoading(false);
    }
  }, [page, limit, search]);

  useEffect(() => {
    load();
  }, [load]);

  const openCreate = () => {
    setForm({ name: "", slug: "", description: "", isActive: true });
    setSlugTouched(false);
    setShowForm(true);
  };

  const openEdit = (cat: Category) => {
    setForm({
      id: cat.id,
      name: cat.name,
      slug: cat.slug,
      description: cat.description || "",
      isActive: cat.isActive ?? true,
    });
    setSlugTouched(true);
    setShowForm(true);
  };

  const toSlug = (input: string) => {
    return input
      .toLowerCase()
      .normalize("NFD")
      .replace(/\p{Diacritic}+/gu, "")
      .replace(/đ/g, "d")
      .replace(/[^a-z0-9\s-]/g, "")
      .trim()
      .replace(/\s+/g, "-")
      .replace(/-+/g, "-");
  };

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    try {
      const payload = {
        name: form.name.trim(),
        slug: form.slug.trim() || undefined,
        description: form.description.trim() || undefined,
        isActive: form.isActive,
      } as any;
      if (!payload.name) {
        setError("Vui lòng nhập tên danh mục");
        setLoading(false);
        return;
      }
      if (isEditing && form.id) {
        await CategoriesService.updateCategory(form.id, payload);
      } else {
        await CategoriesService.createCategory(payload);
      }
      setShowForm(false);
      setForm({ name: "", slug: "", description: "", isActive: true });
      await load();
    } catch (e: any) {
      setError(e?.message || "Lưu danh mục thất bại");
    } finally {
      setLoading(false);
    }
  };

  const onDelete = (id: number) => {
    setDeleteId(id);
    setShowDelete(true);
  };

  const onConfirmDelete = async () => {
    if (deleteId == null) return;
    setLoading(true);
    setError("");
    try {
      await CategoriesService.deleteCategory(deleteId);
      setShowDelete(false);
      setDeleteId(null);
      await load();
    } catch (e: any) {
      const errorMessage = e?.message || "Xóa thất bại";
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const onBulkDelete = () => {
    if (selectedIds.length === 0) return;
    setDeleteId(-1);
    setShowDelete(true);
  };

  const onConfirmBulkDelete = async () => {
    if (selectedIds.length === 0) return;
    setLoading(true);
    setError("");
    try {
      await Promise.all(
        selectedIds.map((id) => CategoriesService.deleteCategory(id))
      );
      setSelectedIds([]);
      setShowDelete(false);
      setDeleteId(null);
      await load();
    } catch (e: any) {
      const errorMessage = e?.message || "Xóa thất bại";
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const onBulkActivate = async (active: boolean) => {
    if (selectedIds.length === 0) return;
    setLoading(true);
    setError("");
    try {
      await Promise.all(
        selectedIds.map((id) =>
          CategoriesService.updateCategory(id, { isActive: active })
        )
      );
      await load();
    } catch (e: any) {
      setError(e?.message || "Cập nhật thất bại");
    } finally {
      setLoading(false);
    }
  };

  const onSearch = (e: React.FormEvent) => {
    e.preventDefault();
    setPage(1);
    load();
  };

  return (
    <Layout>
      <div className="categories-page">
        <div className="categories-header">
          <div>
            <h2 className="cat-title">Quản lý danh mục</h2>
            <p className="cat-subtitle">
              Tạo, chỉnh sửa và xóa danh mục bài viết
            </p>
          </div>
          <button
            className="btn-primary btn-add-custom"
            onClick={openCreate}
            disabled={loading}
          >
            <Plus size={18} />
            <span>Thêm danh mục</span>
          </button>
        </div>

        <form className="categories-filters" onSubmit={onSearch}>
          <div className="search-box">
            <Search size={18} />
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Tìm kiếm tên hoặc slug"
            />
          </div>
          {/* Nhấn Enter để tìm */}
        </form>

        {error && <div className="cat-error">{error}</div>}

        {/* Bulk actions */}
        {selectedIds.length > 0 && (
          <div className="cat-bulk-bar">
            <span>Đã chọn {selectedIds.length}</span>
            <div className="card-actions">
              <button
                type="button"
                className="btn"
                disabled={loading}
                onClick={() => onBulkActivate(true)}
              >
                Kích hoạt tất cả
              </button>
              <button
                type="button"
                className="btn"
                disabled={loading}
                onClick={() => onBulkActivate(false)}
              >
                Hủy kích hoạt tất cả
              </button>
              <button
                type="button"
                className="btn-danger"
                disabled={loading}
                onClick={onBulkDelete}
              >
                Xóa tất cả
              </button>
            </div>
          </div>
        )}

        <div className="categories-table-card">
          <div className="categories-table-scroll">
            <table className="categories-table">
              <thead>
                <tr>
                  <th style={{ width: 36 }}>
                    <input
                      type="checkbox"
                      checked={allSelected}
                      onChange={toggleSelectAll}
                      aria-label="Chọn tất cả"
                    />
                  </th>
                  <th>ID</th>
                  <th>Tên</th>
                  <th>Slug</th>
                  <th>Mô tả</th>
                  <th>Trạng thái</th>
                  <th>Hành động</th>
                </tr>
              </thead>
              <tbody>
                {items.map((c) => (
                  <tr key={c.id}>
                    <td>
                      <input
                        type="checkbox"
                        checked={selectedIds.includes(c.id)}
                        onChange={() => toggleSelect(c.id)}
                        aria-label={`Chọn ${c.name}`}
                      />
                    </td>
                    <td>CM{c.id.toString().padStart(3, '0')}</td>
                    <td>{c.name}</td>
                    <td>{c.slug}</td>
                    <td className="cat-ellipsis">{c.description || "-"}</td>
                    <td>
                      {(() => {
                        const active = c.isActive ?? true;
                        return (
                          <span
                            className={`terms-badge ${
                              active
                                ? "terms-badge-active"
                                : "terms-badge-inactive"
                            }`}
                          >
                            {active ? "Hiệu lực" : "Không hiệu lực"}
                          </span>
                        );
                      })()}
                    </td>
                    <td>
                      <div className="card-actions">
                        <button
                          className="btn-edit"
                          title="Sửa"
                          onClick={() => openEdit(c)}
                        >
                          <Pencil size={16} />
                        </button>
                        <button
                          className="btn-danger "
                          title="Xóa"
                          onClick={() => onDelete(c.id)}
                        >
                          <Trash2 size={16} color="currentColor" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
                {items.length === 0 && !loading && (
                  <tr>
                    <td colSpan={6} className="terms-empty">
                      <div>
                        <img src={emptyDataImg} alt="Dữ liệu trống" />
                      </div>
                      <div>Không có dữ liệu</div>{" "}
                    </td>
                  </tr>
                )}
                {loading && (
                  <tr>
                    <td colSpan={6} className="terms-loading">
                      Đang tải...
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>

        {/* Pagination */}
        <div className="cat-pagination">
          <button
            className="btn"
            disabled={page <= 1 || loading}
            onClick={() => setPage((p) => Math.max(1, p - 1))}
          >
            Trước
          </button>
          <span>
            Trang {page} / {totalPages}
          </span>
          <button
            className="btn"
            disabled={page >= totalPages || loading}
            onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
          >
            Sau
          </button>
        </div>

        {/* Modal */}
        {showForm && (
          <div className="modal-overlay">
            <div className="terms-modal">
              <div className="terms-modal-header">
                <h3 className="terms-modal-title">
                  {isEditing ? "Cập nhật danh mục" : "Tạo danh mục"}
                </h3>
                <button
                  className="btn terms-icon-btn"
                  onClick={() => setShowForm(false)}
                >
                  <X size={16} />
                </button>
              </div>
              <form onSubmit={onSubmit}>
                <div className="terms-modal-row">
                  <div className="terms-modal-info">
                    <label>Tên danh mục</label>
                    <input
                      className="cat-input"
                      value={form.name}
                      onChange={(e) => {
                        const newName = e.target.value;
                        setForm((s) => ({
                          ...s,
                          name: newName,
                          slug: !slugTouched ? toSlug(newName) : s.slug,
                        }));
                      }}
                      placeholder="VD: Công nghệ"
                    />
                  </div>
                  <div className="terms-modal-info">
                    <label>Slug</label>
                    <input
                      className="cat-input"
                      value={form.slug}
                      onChange={(e) => {
                        setForm((s) => ({ ...s, slug: e.target.value }));
                        setSlugTouched(true);
                      }}
                      onBlur={() =>
                        setForm((s) => ({
                          ...s,
                          slug: s.slug ? toSlug(s.slug) : toSlug(s.name),
                        }))
                      }
                      placeholder="VD: cong-nghe"
                    />
                  </div>
                </div>
                <div className="terms-modal-desc">
                  <label>Mô tả</label>
                  <textarea
                    rows={5}
                    value={form.description}
                    onChange={(e) =>
                      setForm((s) => ({ ...s, description: e.target.value }))
                    }
                    placeholder="Mô tả danh mục"
                    className="textarea-common"
                  />
                </div>
                <div className="terms-inline-row">
                  <input
                    id="isActive"
                    type="checkbox"
                    checked={form.isActive}
                    onChange={(e) =>
                      setForm((s) => ({ ...s, isActive: e.target.checked }))
                    }
                  />
                  <label htmlFor="isActive">Kích hoạt</label>
                </div>

                <div className="modal-actions">
                  <button
                    type="button"
                    className="btn-cancel"
                    onClick={() => setShowForm(false)}
                  >
                    Hủy
                  </button>
                  <button
                    type="submit"
                    className="btn-primary"
                    disabled={loading}
                  >
                    {isEditing ? "Cập nhật" : "Tạo mới"}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        <ConfirmDelete
          isOpen={showDelete}
          title="Xác nhận xóa danh mục"
          message={(() => {
            if (deleteId === -1) {
              return (
                <span>
                  Bạn có chắc chắn muốn xóa <b>{selectedIds.length}</b> danh mục
                  đã chọn? Hành động này không thể hoàn tác.
                </span>
              );
            }
            const name = items.find((i) => i.id === deleteId)?.name;
            return name ? (
              <span>
                Bạn có chắc chắn muốn xóa danh mục <b>{name}</b>? Hành động này
                không thể hoàn tác.
              </span>
            ) : (
              "Bạn có chắc chắn muốn xóa danh mục này? Hành động này không thể hoàn tác."
            );
          })()}
          confirmText="Xóa"
          cancelText="Hủy"
          isSubmitting={loading}
          onConfirm={deleteId === -1 ? onConfirmBulkDelete : onConfirmDelete}
          onCancel={() => {
            setShowDelete(false);
            setDeleteId(null);
          }}
        />
      </div>
    </Layout>
  );
};

export default Categories;
